/* eslint-disable no-underscore-dangle, camelcase */
import {
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
  ResourceType,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';

const useProgram = ({
  programId,
  resourceType,
}: {
  programId: number,
  resourceType: ResourceType,
}) => {
  const dispatch = useDispatch();
  const isPersonal = resourceType === 'personalPrograms';
  const programType = isPersonal ? 'PersonalProgram' : 'ProgramTemplate';
  const mapState = useCallback((state) => {
    const program = coreSelectors.getResourceById(
      resourceType,
      programId,
    )(state);
    return {
      program,
      weeks: coreSelectors.getProgamWeeks(
        programId,
        resourceType,
      )(state).filter(item => item.id > 0),
      programWorkouts: coreSelectors.getResourceMappedList(
        'programWorkouts',
        `program_${programId}`,
      )(state),
    };
  }, [programId, resourceType]);

  const state = useSelector(mapState, shallowEqual);

  const requestUpdateProgram = useCallback((payload) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: programId,
        payload,
        resourceType,
      }),
    );
  }, [programId, resourceType]);

  const requestAddWeek = useCallback(() => {
    dispatch(
      coreActions.requestAddProgramWeek({
        programId,
        programType,
      }),
    );
    requestUpdateProgram({
      weeks_count: state.program.weeks_count + 1,
    });
  }, [programId, resourceType]);

  const requestAddWorkout = useCallback((option) => {
    // add to the last week
    const last = [...state.weeks].pop();
    const position = state.programWorkouts.filter(w => w.week_id === last?.id).length + 1;
    const onSuccess = (wk) => {
      dispatch(
        uiActions.setUiState({
          selectedProgramWorkouts: {
            _merge: true,
            [wk.program_id]: {
              id: wk.id,
              weekId: wk.week_id,
            },
          },
        }),
      );
    };

    const data = (option.__isNew__ || option.newOne)
      ? {
        name: option.__isNew__ ? option.value : null,
        week_id: last ? last.id : null,
      }
      : {
        week_id: last ? last.id : null,
        [option.isPersonalWorkout ? 'personal_workout_id' : 'workout_template_id']: +option.value,
      };

    dispatch(
      coreActions.requestAddProgramWorkout({
        payload: {
          position,
          program_id: programId,
          program_type: programType,
          ...data,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  }, [programId, resourceType, state.weeks.length, state.programWorkouts.length]);

  const requestSaveAsTemplate = useCallback((values, callbacks) => {
    dispatch(
      coreActions.requestSaveAsTemplate({
        id: programId,
        payload: {
          folder_id: values.folderId,
        },
        callbacks,
        resourceType: 'programTemplates',
      }),
    );
  }, [programId, resourceType]);

  return {
    requestAddWeek,
    requestAddWorkout,
    requestSaveAsTemplate,
    requestUpdateProgram,
  };
};

export default useProgram;
